import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageBackground from "@/components/PageBackground";
import { Wallet, ArrowDownToLine, TrendingUp, Clock, X } from "lucide-react";
import { toast } from "sonner";
import PushOptIn from "@/components/PushOptIn";
import { usePricing, finalCashbackRate } from "@/lib/pricing";

export default function UserWallet() {
  const pricing = usePricing();
  const cashbackRate = finalCashbackRate(pricing);
  const [data, setData] = useState(null);
  const [txs, setTxs] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const loadAll = async () => {
    const [{ data: w }, { data: t }, { data: p }] = await Promise.all([
      api.get("/wallet/me"),
      api.get("/wallet/transactions"),
      api.get("/wallet/payouts"),
    ]);
    setData(w); setTxs(t); setPayouts(p);
  };
  useEffect(() => { loadAll(); }, []);

  const withdraw = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/wallet/withdraw", { amount: Number(amount) });
      toast.success("Withdrawal requested — we'll process within 3 business days.");
      setShowModal(false); setAmount(""); await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not withdraw");
    } finally { setBusy(false); }
  };

  if (!data) return <div className="relative"><PageBackground variant="portal" /><div className="max-w-4xl mx-auto px-6 py-24 relative z-10 text-slate-500">Loading…</div></div>;
  const w = data.wallet;
  const kpis = data.kpis || {};
  const cfg = data.config || { min_withdrawal: 20, holding_period_days: 14 };

  return (
    <div className="relative">
      <PageBackground variant="portal" />
      <div className="max-w-6xl mx-auto px-6 py-12 relative z-10" data-testid="user-wallet">
        <div className="pf-chip pf-chip-mint w-fit"><Wallet className="w-3 h-3" /> Cashback wallet</div>
        <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight mt-3">
          Your <span className="pf-gradient-text-2">Cyannect cashback</span>
        </h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl" data-testid="wallet-cashback-rate-copy">
          {pricing === undefined ? null : cashbackRate
            ? <>Earn <span className="font-semibold">{cashbackRate}</span> cashback on every verified purchase — automatic, on every store.</>
            : <>Earn <span className="font-semibold">0.75%</span> on purchases ≤ CAD $200 and <span className="font-semibold">1%</span> on purchases &gt; CAD $200 — automatic after every verified purchase.</>}
          {" "}Cashback becomes available after a {cfg.holding_period_days}-day approval window.
        </p>

        <div className="mt-6">
          <PushOptIn />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8">
          <div className="pf-card p-5" data-testid="wallet-kpi-available">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1"><Wallet className="w-3 h-3" /> Available cashback</div>
            <div className="font-display text-3xl font-medium mt-2 pf-gradient-text-2 tabular-nums">CAD ${(kpis.available_cashback || 0).toFixed(2)}</div>
            <button onClick={() => setShowModal(true)} disabled={(kpis.available_cashback || 0) < cfg.min_withdrawal} className="mt-3 pf-btn-primary px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-50" data-testid="wallet-withdraw-btn">
              <ArrowDownToLine className="w-3 h-3" /> Withdraw
            </button>
          </div>
          <div className="pf-card p-5" data-testid="wallet-kpi-pending">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1"><Clock className="w-3 h-3" /> Pending cashback</div>
            <div className="font-display text-3xl font-medium mt-2 tabular-nums text-amber-600">CAD ${(kpis.pending_cashback || 0).toFixed(2)}</div>
            <div className="text-[10px] text-slate-500 mt-1">Approves after {cfg.holding_period_days}-day window</div>
          </div>
          <div className="pf-card p-5" data-testid="wallet-kpi-lifetime">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Lifetime cashback</div>
            <div className="font-display text-3xl font-medium mt-2 tabular-nums">CAD ${(kpis.lifetime_cashback || 0).toFixed(2)}</div>
          </div>
          <div className="pf-card p-5" data-testid="wallet-kpi-savings">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Total purchase value</div>
            <div className="font-display text-3xl font-medium mt-2 tabular-nums">CAD ${(kpis.total_savings || 0).toFixed(2)}</div>
            <div className="text-[10px] text-slate-500 mt-1">Across {kpis.offers_used || 0} offers</div>
          </div>
        </div>

        <div className="pf-card overflow-hidden mt-8">
          <div className="p-4 text-xs uppercase tracking-widest text-slate-500 font-semibold">Recent transactions</div>
          <table className="w-full text-sm">
            <thead className="bg-white/40 text-left text-xs uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-5 py-2">When</th>
                <th className="px-5 py-2">Description</th>
                <th className="px-5 py-2">Source</th>
                <th className="px-5 py-2 text-right">Amount</th>
                <th className="px-5 py-2 text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              {txs.map((t) => (
                <tr key={t.id} className="border-t border-white/60" data-testid={`wallet-tx-${t.id}`}>
                  <td className="px-5 py-2 text-xs text-slate-500">{new Date(t.created_at).toLocaleString()}</td>
                  <td className="px-5 py-2">{t.description}</td>
                  <td className="px-5 py-2"><span className="pf-chip">{t.source}</span>{t.status && t.status !== "approved" && <span className={`ml-1 pf-chip text-[10px] ${t.status === "pending" ? "pf-chip-lav" : t.status === "refunded" ? "" : "pf-chip-mint"}`}>{t.status}</span>}</td>
                  <td className={`px-5 py-2 text-right tabular-nums font-semibold ${(t.amount ?? 0) > 0 ? "text-cyan-700" : "text-red-600"}`}>{(t.amount ?? 0) > 0 ? "+" : ""}${(t.amount ?? 0).toFixed(2)}</td>
                  <td className="px-5 py-2 text-right tabular-nums text-slate-500 text-xs">{t.balance_after != null ? `$${t.balance_after.toFixed(2)}` : "—"}</td>
                </tr>
              ))}
              {txs.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-500">No transactions yet — shop with cashback-enabled coupons to start earning.</td></tr>}
            </tbody>
          </table>
        </div>

        {payouts.length > 0 && (
          <div className="pf-card overflow-hidden mt-6">
            <div className="p-4 text-xs uppercase tracking-widest text-slate-500 font-semibold">Withdrawal history</div>
            <table className="w-full text-sm">
              <thead className="bg-white/40 text-left text-xs uppercase tracking-widest text-slate-500">
                <tr><th className="px-5 py-2">Date</th><th className="px-5 py-2">Amount</th><th className="px-5 py-2">Status</th></tr>
              </thead>
              <tbody>
                {payouts.map((p) => (
                  <tr key={p.id} className="border-t border-white/60" data-testid={`wallet-payout-${p.id}`}>
                    <td className="px-5 py-2 text-xs text-slate-500">{new Date(p.created_at).toLocaleString()}</td>
                    <td className="px-5 py-2 tabular-nums font-semibold">${(p.amount ?? 0).toFixed(2)}</td>
                    <td className="px-5 py-2"><span className={`pf-chip ${p.status === "paid" ? "pf-chip-mint" : ""}`}>{p.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-md p-4" onClick={() => setShowModal(false)} data-testid="withdraw-modal">
          <form onSubmit={withdraw} onClick={(e) => e.stopPropagation()} className="glass-strong rounded-3xl w-full max-w-md p-8 relative">
            <button type="button" onClick={() => setShowModal(false)} className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-white/70 text-slate-500"><X className="w-4 h-4" /></button>
            <div className="pf-chip pf-chip-mint w-fit"><ArrowDownToLine className="w-3 h-3" /> Withdraw</div>
            <h3 className="font-display text-2xl mt-3">Withdraw cashback</h3>
            <p className="text-sm text-slate-600 mt-2">Available: <span className="font-semibold">CAD ${w.balance.toFixed(2)}</span>. Minimum CAD ${cfg.min_withdrawal.toFixed(2)}.</p>
            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mt-5 mb-2">Amount ($)</label>
            <input required type="number" step="0.01" min={cfg.min_withdrawal} max={w.balance} value={amount} onChange={(e) => setAmount(e.target.value)} className="pf-input w-full px-4 py-3 text-sm" data-testid="withdraw-amount" />
            <button type="submit" disabled={busy} className="mt-5 pf-btn-primary w-full py-3 rounded-full font-semibold text-sm disabled:opacity-60" data-testid="withdraw-submit">
              {busy ? "Requesting…" : "Request withdrawal"}
            </button>
            <p className="text-[11px] text-slate-500 mt-3">Payouts processed within 3 business days via e-Transfer / PayPal (mocked in dev).</p>
          </form>
        </div>
      )}
    </div>
  );
}
